import * as React from "react";

import { cn } from "@/lib/utils";

type HeadingSize = "xl" | "lg" | "md" | "sm";

const headingScale: Record<HeadingSize, string> = {
  xl: "text-3xl sm:text-4xl font-semibold leading-tight tracking-tight",
  lg: "text-2xl sm:text-3xl font-semibold leading-snug",
  md: "text-xl sm:text-2xl font-semibold leading-snug",
  sm: "text-lg font-semibold leading-snug",
};

type PolymorphicProps<T extends React.ElementType> = {
  as?: T;
  size?: HeadingSize;
  className?: string;
  children: React.ReactNode;
} & Omit<React.ComponentPropsWithoutRef<T>, "as" | "children">;

/**
 * Semantic, size-aware heading. Defaults to an h1 to encourage ARIA-friendly usage.
 */
export function Heading<T extends React.ElementType = "h1">({
  as,
  size = "xl",
  className,
  children,
  ...props
}: PolymorphicProps<T>) {
  const Component = (as ?? "h1") as React.ElementType;

  return (
    <Component className={cn(headingScale[size], className)} {...props}>
      {children}
    </Component>
  );
}

/**
 * Secondary heading sized for section titles.
 */
export function Subheading<T extends React.ElementType = "h2">({
  as,
  size = "md",
  className,
  children,
  ...props
}: PolymorphicProps<T>) {
  const Component = (as ?? "h2") as React.ElementType;

  return (
    <Component className={cn(headingScale[size], className)} {...props}>
      {children}
    </Component>
  );
}
